<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import type { Study } from '@pocketprep/types'
import UIKit from '@pocketprep/ui-kit'

const {
    Checkbox,
    Icon,
} = UIKit

const props = withDefaults(defineProps<{
    subjects?: Study.Class.SubjectJSON[]
    initialShowSubjects?: boolean
    initialSelectedSubjects?: string[]
    disabled?: boolean
}>(), {
    subjects: () => [],
    initialShowSubjects: true,
    initialSelectedSubjects: () => [],
    disabled: false,
})

const emit = defineEmits<{
    'selectedSubjects': [ selectedSubjects: string[] ]
}>()

const selectedSubjects = ref<string[]>([])
const showSubjects = ref(true)

const allSubjectsSelected = computed(() => {
    return selectedSubjects.value?.length === props.subjects?.length
        ? true
        : selectedSubjects.value?.length
            ? null
            : false
})

const selectAllSubjects = () => {
    if (allSubjectsSelected.value) {
        selectedSubjects.value = []
    } else {
        selectedSubjects.value = props.subjects.map(s => s.objectId)
    }

    emit('selectedSubjects', selectedSubjects.value)
}

const clickSubject = (subjectId: string) => {
    if (selectedSubjects.value.includes(subjectId)) {
        selectedSubjects.value.splice(selectedSubjects.value.indexOf(subjectId), 1)
    } else {
        selectedSubjects.value = [ ...selectedSubjects.value, subjectId ]
    }

    emit('selectedSubjects', selectedSubjects.value)
}

onMounted(() => {
    selectedSubjects.value = props.initialSelectedSubjects
    showSubjects.value = props.initialShowSubjects
    emit('selectedSubjects', selectedSubjects.value)
})

watch(() => props.initialSelectedSubjects, () => {
    selectedSubjects.value = props.initialSelectedSubjects
})
</script>

<template>
    <div class="subjects-selector">
        <div class="subjects-selector__subjects-head">
            <div
                class="subjects-selector__subjects-head-left"
                tabindex="0"
                role="button"
                :aria-expanded="showSubjects ? 'true' : 'false'"
                @click="showSubjects = !showSubjects"
                @keydown.enter="showSubjects = !showSubjects"
                @mousedown.prevent
            >
                <Icon
                    type="subject"
                    class="subjects-selector__subjects-head-left-subject"
                />
                <label
                    id="subjects-selector__subjects-head-left-subject-label"
                    class="subjects-selector__subjects-head-left-subject-label"
                >
                    Subjects
                </label>
                <Icon
                    type="accordionArrow"
                    class="subjects-selector__subjects-head-left-accordion"
                    :class="{ 'subjects-selector__subjects-head-left-accordion--asc': showSubjects }"
                />
            </div>
            <div v-if="selectedSubjects" class="subjects-selector__subjects-head-right">
                <div class="subjects-selector__subjects-count">
                    {{ selectedSubjects && selectedSubjects.length }} of {{ subjects && subjects.length }}
                </div>
                <Checkbox
                    :modelValue="allSubjectsSelected"
                    :disabled="disabled"
                    class="subjects-selector__subjects-head-checkbox"
                    aria-labelledby="subjects-selector__subjects-head-left-subject-label"
                    @click.stop="!disabled && selectAllSubjects()"
                    @keydown.enter="!disabled && selectAllSubjects()"
                    @mousedown.prevent
                />
            </div>
        </div>
        <div
            v-if="showSubjects"
            class="subjects-selector__subject-options"
            aria-live="polite"
        >
            <div
                v-for="subject in subjects"
                :key="subject.objectId"

                class="subjects-selector__subject-option"
                :class="{
                    'subjects-selector__subject-option--selected': selectedSubjects.includes(subject.objectId),
                    'subjects-selector__subject-option--disabled': disabled
                }"
                @click="!disabled && clickSubject(subject.objectId)"
                @keydown.enter="!disabled && clickSubject(subject.objectId)"
                @mousedown.prevent
            >
                <Checkbox
                    :modelValue="selectedSubjects.includes(subject.objectId)"
                    :disabled="disabled"
                    class="subjects-selector__subject-option-checkbox"
                    :aria-labelledby="`subjects-selector__label-${subject.objectId}`"
                    @click.stop="!disabled && clickSubject(subject.objectId)"
                />
                <label
                    :id="`subjects-selector__label-${subject.objectId}`"
                    class="subjects-selector__subject-option-label"
                    :class="{'subjects-selector__subject-option-label--disabled': disabled}"
                >
                    {{ subject.name }}
                </label>
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.subjects-selector {
    border: 1px solid $gray-divider;
    background: $white;
    border-radius: 6px;

    &__subject-option {
        font-size: 15px;
        line-height: 19px;
        color: $slate-01;
        padding: 14px 50px 13px 15px;
        border-top: 1px solid $gray-divider;
        cursor: pointer;
        user-select: none;
        position: relative;

        &--selected {
            color: $brand-black;
        }

        &--disabled {
            cursor: default!important;
        }

        &:last-child {
            border-bottom: 0;
        }

        svg {
            color: $brand-blue;
            position: absolute;
            width: 16px;
            height: 13px;
            top: 19px;
            right: 20px;
        }
    }

    &__subject-option-checkbox {
        position: absolute;
        top: 50%;
        margin-top: -10.5px;
        right: 12px;
    }

    &__subject-option-label {
        cursor: pointer;

        &--disabled {
            cursor: default!important;
        }
    }

    &__subjects-head {
        display: flex;
        justify-content: space-between;
        padding: 17.5px 12px 17.5px 15px;
        align-items: center;
    }

    &__subjects-head-left {
        cursor: pointer;
        user-select: none;
        display: flex;
        align-items: center;
        flex: 1;
        outline: none;
        position: relative;

        span {
            display: block;
            font-size: 15px;
            line-height: 20px;
        }

        &:focus::before {
            content: '';
            left: -4px;
            top: -4px;
            width: 100%;
            height: calc(100% + 8px);
            border: 1px solid $brand-blue;
            position: absolute;
            border-radius: 5px;
        }
    }

    &__subjects-head-left-subject {
        margin-right: 8px;
        height: 21px;
        margin-bottom: 2px;
    }

    &__subjects-head-left-subject-label {
        cursor: pointer;
        font-size: 15px;
        line-height: 20px;
    }

    &__subjects-head-left-accordion {
        color: $brand-blue;
        margin-left: 6px;

        &--asc {
            transform: rotate(180deg);
        }
    }

    &__subjects-head-right {
        display: flex;
        align-items: center;
        color: $slate-01;
        font-size: 14px;
        line-height: 17px;
    }

    &__subjects-count {
        margin-right: 8px;
    }
}
</style>
